import { memo, useEffect, useMemo, useRef } from "react";
import { noop } from "lodash-es";

import Box from "ds/components/Box";
import { PollingIntervalGroups } from "apollo/constants";
import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchQueryPredicate } from "types/generated";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { FILTERS_ORDER_SETTINGS_KEY, initialSortDirection, initialSortOption } from "../constants";
import { SEARCH_BLUEPRINT_VERSIONED_GROUPS_SUGGESTIONS } from "../gql";
import { templatesSuggestionsDictionary, sortItemOrder } from "./constants";

type TemplatesFiltersLayoutProps = {
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  setItemsCount: (count: number) => void;
  itemsCount: number;
  titleId?: string;
};

const TemplatesFiltersLayout = ({
  predicates,
  children,
  setItemsCount,
  itemsCount,
}: TemplatesFiltersLayoutProps) => {
  const fieldsRef = useRef<string[] | null>(null);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const { onError } = useTypedContext(FlashContext);

  const [loadBlueprintsSuggestions, { data: filteringData, loading, refetch }] = usePolledLazyQuery(
    SEARCH_BLUEPRINT_VERSIONED_GROUPS_SUGGESTIONS,
    { pollingGroup: PollingIntervalGroups.Lists }
  );

  const cachedFiltersData = useCachedFilterFields(
    filteringData?.searchBlueprintVersionedGroupsSuggestions?.fields
  );

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter(
          (field) => field.orderable && field.name !== "slug" && sortItemOrder.includes(field.name)
        )
        .map((field) => {
          return {
            value: field.name,
            label: templatesSuggestionsDictionary[field.name] || field.name,
          };
        })
        .sort((a, b) => sortItemOrder.indexOf(a.value) - sortItemOrder.indexOf(b.value)) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: templatesSuggestionsDictionary,
  });

  const loadSearchSuggestions = (initial = false) => {
    try {
      loadBlueprintsSuggestions({
        variables: {
          input: {
            fullTextSearch: searchInput,
            predicates,
            fields: initial ? null : fieldsRef.current,
          },
        },
      }).then(({ data }) => {
        if (
          data?.searchBlueprintVersionedGroupsSuggestions.filteredCount &&
          data?.searchBlueprintVersionedGroupsSuggestions.filteredCount !== itemsCount
        ) {
          setItemsCount(data?.searchBlueprintVersionedGroupsSuggestions.filteredCount);
        }
      });
    } catch (error) {
      onError(error);
    }
  };

  const handlePollingActiveSections = (fields: string[]) => {
    fieldsRef.current = fields;

    refetch({
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields,
      },
    }).then(({ data }) => {
      if (
        data?.searchBlueprintVersionedGroupsSuggestions.filteredCount &&
        data?.searchBlueprintVersionedGroupsSuggestions.filteredCount !== itemsCount
      ) {
        setItemsCount(data?.searchBlueprintVersionedGroupsSuggestions.filteredCount);
      }
    });
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersDictionary={templatesSuggestionsDictionary}
      filtersType="templatesWorkbench"
      setCurrentSavedView={noop}
    >
      <Box fullHeight fullWidth direction="column">
        {children}
      </Box>
    </Filters>
  );
};

export default memo(TemplatesFiltersLayout);
